import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  ShieldCheck,
  XCircle,
} from "lucide-react";

import type { ScannerController } from "../useScannerController";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export default function AuthPhase({ c }: { c: ScannerController }) {
  const detected = c.isAuthRequired;
  const secretType = c.showSecret ? "text" : "password";

  return (
    <div className="mx-auto max-w-2xl p-5">
      <Card className="shadow-none overflow-hidden">
        <div className="border-b border-border bg-gradient-to-br from-warning/10 via-transparent to-transparent p-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-warning/15 text-warning">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold">
                {detected ? "Login required" : "Provide credentials"}
              </h2>
              <p className="text-[12px] text-muted-foreground">
                {detected
                  ? "The crawler hit a login wall. Enter credentials so the agent can reach protected pages."
                  : "Enter the credentials the agent should use while crawling protected areas."}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-5">
          {c.authStrategy === "form" && (
            <>
              <Field label="Login URL (optional)">
                <Input
                  value={c.loginUrl}
                  onChange={(e) => c.setLoginUrl(e.target.value)}
                  placeholder="https://app.example.com/login"
                  className="h-10 font-mono text-[13px]"
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Username or email">
                  <Input
                    value={c.username}
                    onChange={(e) => c.setUsername(e.target.value)}
                    placeholder="qa.bot@company.com"
                    className="h-10 text-[13px]"
                  />
                </Field>
                <Field label="Password">
                  <div className="relative">
                    <Input
                      type={secretType}
                      value={c.password}
                      onChange={(e) => c.setPassword(e.target.value)}
                      placeholder={
                        c.hasSavedPassword
                          ? "Leave blank to reuse saved password"
                          : "Enter password"
                      }
                      className="h-10 pr-9 text-[13px]"
                    />
                    <SecretToggle
                      show={c.showSecret}
                      onToggle={() => c.setShowSecret(!c.showSecret)}
                    />
                  </div>
                </Field>
              </div>
            </>
          )}

          {c.authStrategy === "token" && (
            <Field label="Bearer token">
              <div className="relative">
                <Input
                  type={secretType}
                  value={c.token}
                  onChange={(e) => c.setToken(e.target.value)}
                  placeholder="eyJhbGciOi…"
                  className="h-10 pr-9 font-mono text-[13px]"
                />
                <SecretToggle
                  show={c.showSecret}
                  onToggle={() => c.setShowSecret(!c.showSecret)}
                />
              </div>
            </Field>
          )}

          {c.authStrategy === "api_key" && (
            <div className="grid gap-4 sm:grid-cols-[1fr_1.4fr]">
              <Field label="Header name">
                <Input
                  value={c.apiKeyHeader}
                  onChange={(e) => c.setApiKeyHeader(e.target.value)}
                  placeholder="X-API-Key"
                  className="h-10 font-mono text-[13px]"
                />
              </Field>
              <Field label="API key">
                <div className="relative">
                  <Input
                    type={secretType}
                    value={c.token}
                    onChange={(e) => c.setToken(e.target.value)}
                    placeholder="sk_live_…"
                    className="h-10 pr-9 font-mono text-[13px]"
                  />
                  <SecretToggle
                    show={c.showSecret}
                    onToggle={() => c.setShowSecret(!c.showSecret)}
                  />
                </div>
              </Field>
            </div>
          )}

          {/* Test connection */}
          <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
            <div className="min-w-0 flex-1">
              {c.testConnStatus === "idle" && (
                <span className="text-[12px] text-muted-foreground">
                  Verify the credentials before scanning.
                </span>
              )}
              {c.testConnStatus === "testing" && (
                <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Testing
                  connection…
                </span>
              )}
              {c.testConnStatus === "success" && (
                <span className="inline-flex items-center gap-1.5 text-[12px] text-success">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {c.testConnMessage}
                </span>
              )}
              {c.testConnStatus === "failed" && (
                <span className="inline-flex items-start gap-1.5 text-[12px] text-destructive">
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{" "}
                  {c.testConnMessage}
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={c.testConnection}
              disabled={c.testConnStatus === "testing" || !c.url.trim()}
            >
              Test connection
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/20 px-5 py-3">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-success" />
            Encrypted at rest. Used only during the scan.
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={c.backToConnectFromAuth}
              className="gap-1.5"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Button>
            <Button
              size="sm"
              onClick={c.submitAuthAndScan}
              disabled={c.isStarting}
              className="gap-1.5"
            >
              {c.isStarting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArrowRight className="h-3.5 w-3.5" />
              )}
              Continue scanning
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function SecretToggle({
  show,
  onToggle,
}: {
  show: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground",
      )}
      tabIndex={-1}
      aria-label={show ? "Hide secret" : "Show secret"}
    >
      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );
}
